import { FC, useEffect, useState } from 'react'

import { Button, AptiveIcon, Dropdown, DropdownItem } from '@aptive-env/storybook'
import CalendarSearch from 'components/CalendarSearch'
import { CalendarViewValue, ICalendarEventType } from 'interface/calendar'

import MultiEventSelect from 'components/shared/MultiEventSelect'
import CalendarYearView from 'components/CalendarYearView'
import CalendarWeekView from 'components/CalendarWeekView'
import CalendarDayView from 'components/CalendarDayView'
import CalendarMonthView from 'components/CalendarMonthView'

interface View {
  value: CalendarViewValue
  label: string
}

const VIEWS: View[] = [
  {
    value: 'day',
    label: 'Day view'
  },
  {
    value: 'week',
    label: 'Week view'
  },
  {
    value: 'month',
    label: 'Month view'
  },
  {
    value: 'year',
    label: 'Year view'
  },
]

interface OfficeCalendarProps {
  officeId: number
  onClickAddNewEvent: () => void
  onSuccess: (success: string) => void
  eventTypes: ICalendarEventType[]
  activeDate: Date
  viewMode: number
  setViewMode: (mode: number) => void
}

const OfficeCalendar: FC<OfficeCalendarProps> = ({
  officeId,
  onClickAddNewEvent,
  onSuccess,
  eventTypes,
  activeDate,
  viewMode,
  setViewMode,
}) => {
  const [selectedTypes, setSelectedTypes] = useState<ICalendarEventType[]>(eventTypes)
  const [view, setView] = useState<View>(VIEWS[viewMode])
  const [isViewDropdownOpen, setIsViewDropdownOpen] = useState(false)

  useEffect(() => {
    setSelectedTypes(eventTypes)
  }, [eventTypes])

  useEffect(() => {
    setView(VIEWS[viewMode])
  }, [viewMode])

  return (
    <div className="grow p-3 flex flex-col">
      <div className="flex flex-col shadow-med rounded-lg overflow-auto">
        <div className="flex justify-between p-4 bg-[#f9fafb] z-[1]">
          <div className="flex items-center gap-5">
            <MultiEventSelect
              options={eventTypes}
              value={selectedTypes}
              onChange={(value) => setSelectedTypes(value)}
            />
            <CalendarSearch officeId={officeId} key={officeId} onEventSuccess={onSuccess} />
          </div>
          <div className="flex items-center gap-5">
            <div className="relative">
              <Button
                variant="neutral"
                icon="chevronDown"
                position="right"
                className="h-9 bg-white"
                onClick={(event) => {
                  event.stopPropagation()
                  setIsViewDropdownOpen((prev) => !prev)
                }}
              >
                {view.label}
              </Button>
              <Dropdown
                isOpen={isViewDropdownOpen}
                onClose={() => {
                  setIsViewDropdownOpen(false)
                }}
                className="bg-white"
              >
                {VIEWS.map((item, index) => (
                  <DropdownItem
                    key={item.value}
                    onClick={() => {
                      setView(item)
                      setViewMode(index)
                      setIsViewDropdownOpen(false)
                    }}
                  >
                    {item.label}
                  </DropdownItem>
                ))}
              </Dropdown>
            </div>

            <Button
              className="h-[36px] !p-[8px]"
              onClick={onClickAddNewEvent}
            >
              <AptiveIcon className="w-[20px] h-[20px] fill-none" icon="plusCircle" />
              <span className="pl-[4px]">Add new event</span>
            </Button>
          </div>
        </div>

        {/** Calendar */}
        {view.value === 'day' && (
          <CalendarDayView
            currentDate={activeDate}
            eventTypes={selectedTypes}
            officeId={officeId}
            onEventSuccess={onSuccess}
          />
        )}
        {view.value === 'week' && (
          <CalendarWeekView
            currentDate={activeDate}
            eventTypes={selectedTypes}
            officeId={officeId}
            onEventSuccess={onSuccess}
          />
        )}
        {view.value === 'month' && (
          <CalendarMonthView
            officeId={officeId}
            activeDate={activeDate}
            eventTypes={selectedTypes}
            onEventSuccess={onSuccess}
          />
        )}
        {view.value === 'year' && (
          <CalendarYearView
            officeId={officeId}
            year={activeDate.getFullYear()}
            eventTypes={selectedTypes}
          />
        )}
      </div>
    </div>
  )
}

export default OfficeCalendar
